import axios from "axios";

/**
 * The Communication Box: one inbox over WhatsApp and email.
 *
 * Backend app: wsgi/teson/communication, mounted at the API root like the rest
 * of this client, so no `/api` prefix. axios is configured globally in
 * Routers.jsx (base URL, auth token, organization header), so these are thin
 * wrappers that return the raw promise and leave error handling to the caller.
 */

const BASE = "/communication";

export const rowsOf = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  return data?.results || [];
};

const query = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

// -- filter bar -------------------------------------------------------

/** Products a conversation can belong to (shared with activity templates). */
export const loadProducts = () =>
  axios.get(`/payment/pr/${query({ page_size: 500 })}`);

/**
 * The numbers / mailboxes for a product. Passing no product returns every
 * channel, which is what keeps numbers with no product configuration reachable.
 */
export const loadChannels = (params) =>
  axios.get(`${BASE}/channels/${query(params)}`);

// -- conversations ----------------------------------------------------

export const loadConversations = (params) =>
  axios.get(`${BASE}/conversations/${query({ page_size: 100, ...params })}`);

export const loadMessages = (ref, params) =>
  axios.get(
    `${BASE}/conversations/${encodeURIComponent(ref)}/messages/${query(params)}`
  );

export const loadIdentity = (ref) =>
  axios.get(`${BASE}/conversations/${encodeURIComponent(ref)}/identity/`);

/** Website chat only: find an account from details the visitor confirms. */
export const searchAccounts = (ref, q) =>
  axios.get(`${BASE}/conversations/${encodeURIComponent(ref)}/account-search/`, { params: { q } });

/** Website chat only: `accountId` null unlinks. */
export const linkAccount = (ref, accountId) =>
  axios.post(`${BASE}/conversations/${encodeURIComponent(ref)}/link-account/`, {
    account_id: accountId,
  });

export const markRead = (ref) =>
  axios.post(`${BASE}/conversations/${encodeURIComponent(ref)}/mark-read/`);

// -- sending ----------------------------------------------------------

export const sendMessage = (ref, payload) =>
  axios.post(`${BASE}/conversations/${encodeURIComponent(ref)}/send/`, payload);

/** Media and attachments. Uploads are slow, so the timeout is generous. */
export const sendWithFiles = (ref, formData) =>
  axios.post(`${BASE}/conversations/${encodeURIComponent(ref)}/send/`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });

// -- attachments ------------------------------------------------------

/**
 * Path only — never put this straight in a `src`/`href`. It is relative to the
 * API origin and needs the auth header, so it goes through `useAuthedMedia` or
 * `downloadAuthed`.
 */
export const attachmentPath = (attachmentId) =>
  `${BASE}/attachments/${attachmentId}/`;

/**
 * Both sides of every conversation with one lead or account, newest first:
 * `{ lead }` or `{ account }`, plus `before` (an ISO time) for older ones.
 * -> `{ results: [envelope + conversation_ref], next_before }`. Messages the
 * record's activities already show are left out.
 */
export const loadRecordMessages = (params) =>
  axios.get(`${BASE}/record-messages/${query(params)}`);
